import type { ReactNode } from 'react';

import type { SxProps, Theme } from '@mui/material/styles';

import { Box, CircularProgress, Link, Typography } from '@/shared/ui';

import { useUserDetails } from '@/features/_shared/user';
import useDataHubExport from '@/features/data-hub/hooks/useDataHubExport';
import type { ExportStatus } from '@/features/data-hub/types';
import { Dialog, DialogButton, DialogContentText } from '@/shared/ui/overlays/Modal';

import {
  ExportMetaProvider,
  ExportNameProvider,
  ExportPreviewProvider,
  ExportRequestProvider,
  ExportSettingsProvider,
  useExportRequest
} from './exportContexts';

const PREPARING_STATUSES: ExportStatus[] = ['loading', 'downloading', 'processing'];

// Figma uses 16px vertical / 24px horizontal popup padding; MUI's DialogContent
// default is 20px/24px, and the content block itself adds another 12px of
// vertical padding plus an 8px gap between the title row and body copy.
const contentSx: SxProps<Theme> = { py: '16px' };
const actionsSx: SxProps<Theme> = { mt: 0, p: 0 };
const bodyWrapperSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  py: '12px'
};
const titleRowSx: SxProps<Theme> = { display: 'flex', alignItems: 'center', gap: '8px' };
const titleSx: SxProps<Theme> = { fontWeight: 700, lineHeight: 'normal', whiteSpace: 'nowrap' };
const bodySx: SxProps<Theme> = { lineHeight: 'normal', maxWidth: '510px' };
const inlineEmailSx: SxProps<Theme> = { textDecoration: 'underline', display: 'inline' };

export const ExportModalsWrapper = () => {
  const { exportStatus, resetExportStatus } = useExportRequest();

  // Excel, direct downloads and integrations all share the "Preparing your export"
  // dialog; only the Excel flow adds the "we'll send it to your email" note.
  const isPreparing = PREPARING_STATUSES.includes(exportStatus);
  const isEmailedExport = exportStatus === 'loading';
  const isQueued = exportStatus === 'queued';
  const isIntegrationQueued = exportStatus === 'integrationQueued';

  const { userEmail } = useUserDetails();

  return (
    <>
      <Dialog
        contentSx={contentSx}
        open={isPreparing}
        onClose={() => {}}
      >
        <Box sx={bodyWrapperSx}>
          <Box sx={titleRowSx}>
            <CircularProgress size={24} />
            <Typography
              sx={titleSx}
              variant='subtitle2'
            >
              Preparing your export
            </Typography>
          </Box>
          {isEmailedExport && (
            <Typography
              sx={bodySx}
              variant='body1'
            >
              This may take a moment. If it takes longer than expected, we'll send the file to your
              email.
            </Typography>
          )}
        </Box>
      </Dialog>

      <Dialog
        actionsSx={actionsSx}
        buttons={<DialogButton onClick={resetExportStatus}>OK</DialogButton>}
        contentSx={contentSx}
        open={isQueued}
        onClose={resetExportStatus}
      >
        <Box sx={bodyWrapperSx}>
          <Typography
            sx={titleSx}
            variant='subtitle2'
          >
            Your export is on its way
          </Typography>
          <Box>
            <Typography
              sx={bodySx}
              variant='body1'
            >
              The file will be sent to{' '}
              <Link
                color='inherit'
                href={`mailto:${userEmail}`}
              >
                {userEmail}
              </Link>
              .
            </Typography>
            <Typography
              sx={bodySx}
              variant='body1'
            >
              You can also find it in Past Exports when it's ready.
            </Typography>
          </Box>
        </Box>
      </Dialog>

      <Dialog
        buttons={<DialogButton onClick={resetExportStatus}>OK</DialogButton>}
        open={isIntegrationQueued}
        title="We're on it."
        onClose={resetExportStatus}
      >
        <DialogContentText>
          You will get an email shortly to{' '}
          <Typography
            component='span'
            sx={inlineEmailSx}
          >
            {userEmail}
          </Typography>
        </DialogContentText>
      </Dialog>
    </>
  );
};

export const ExportProvider = ({ children }: { children: ReactNode }) => {
  const { settings, preview, request, meta, name } = useDataHubExport();

  return (
    <ExportSettingsProvider value={settings}>
      <ExportPreviewProvider value={preview}>
        <ExportRequestProvider value={request}>
          <ExportMetaProvider value={meta}>
            <ExportNameProvider value={name}>
              {children}
              <ExportModalsWrapper />
            </ExportNameProvider>
          </ExportMetaProvider>
        </ExportRequestProvider>
      </ExportPreviewProvider>
    </ExportSettingsProvider>
  );
};
